import { useEffect, useState } from 'react';

export default function ClimatePage() {
  const [disclosure, setDisclosure] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data for climate disclosure
    setDisclosure({
      institution: "Northern Maple Financial",
      reporting_year: 2024,
      tcfd_alignment: "Substantial",
      emissions_scope_1: 1250,
      emissions_scope_2: 4800,
      emissions_scope_3: 12400000,
      financed_emissions_mortgage: 8900000,
      financed_emissions_corporate: 2800000,
      data_quality_score: 3.2,
      coverage_portfolio: 0.78,
      exposure_flood_zones: 4200000000,
      exposure_wildfire_zones: 1800000000,
      high_carbon_sectors: 3200000000,
      net_zero_target: 2050,
    });
    setLoading(false);
  }, []);

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Guideline B-15 Compliance</p>
          </div>
          <span className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            {disclosure.tcfd_alignment} TCFD Alignment
          </span>
        </div>
      </div>

      {/* Emissions Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">GHG Emissions (tCO2e)</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <EmissionsCard
            title="Scope 1"
            subtitle="Direct emissions"
            value={disclosure.emissions_scope_1.toLocaleString()}
            color="blue"
            methodology="PCAF Category 1"
          />
          <EmissionsCard
            title="Scope 2"
            subtitle="Purchased energy"
            value={disclosure.emissions_scope_2.toLocaleString()}
            color="yellow"
            methodology="Location-based"
          />
          <EmissionsCard
            title="Scope 3"
            subtitle="Financed emissions"
            value={(disclosure.emissions_scope_3 / 1000000).toFixed(1) + 'M'}
            color="red"
            methodology="PCAF Standard"
            warning="Data quality: 3.2 / 5.0"
          />
        </div>
      </div>

      {/* Financed Emissions Breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Financed Emissions Breakdown</h3>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="space-y-4">
            <PortfolioBar
              label="Mortgage Portfolio"
              value={disclosure.financed_emissions_mortgage}
              total={disclosure.emissions_scope_3}
              color="green"
            />
            <PortfolioBar
              label="Corporate Lending"
              value={disclosure.financed_emissions_corporate}
              total={disclosure.emissions_scope_3}
              color="orange"
            />
            <div className="pt-4 border-t">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Portfolio Coverage</span>
                <span className="font-semibold">{(disclosure.coverage_portfolio * 100).toFixed(0)}%</span>
              </div>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${disclosure.coverage_portfolio * 100}%` }}
                />
              </div>
              <p className="text-sm text-amber-600 mt-2">⚠️ Target: {'>'}90% coverage by 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Physical Risk Exposure */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Physical Risk Exposure</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <RiskCard
            title="Flood Zones"
            exposure={disclosure.exposure_flood_zones}
            percentage={(disclosure.exposure_flood_zones / 45000000000 * 100).toFixed(1)}
            icon="🌊"
          />
          <RiskCard
            title="Wildfire Zones"
            exposure={disclosure.exposure_wildfire_zones}
            percentage={(disclosure.exposure_wildfire_zones / 45000000000 * 100).toFixed(1)}
            icon="🔥"
          />
        </div>
      </div>

      {/* Transition Risk */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Transition Risk</h3>
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-2xl font-bold text-gray-900">
                ${(disclosure.high_carbon_sectors / 1000000000).toFixed(1)}B
              </p>
              <p className="text-sm text-gray-600">High-carbon sector exposure</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                {((disclosure.high_carbon_sectors / 45000000000) * 100).toFixed(1)}%
              </p>
              <p className="text-sm text-gray-600">of total assets</p>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Oil & Gas</span>
              <span className="font-medium">$1.8B</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Utilities (carbon-intensive)</span>
              <span className="font-medium">$1.4B</span>
            </div>
          </div>
        </div>
      </div>

      {/* Net Zero Commitment */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Net Zero Commitment</h3>
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
          <div className="flex items-center space-x-4">
            <div className="text-4xl">🎯</div>
            <div className="flex-1">
              <p className="text-lg font-semibold text-gray-900">Net Zero by {disclosure.net_zero_target}</p>
              <p className="text-sm text-gray-600 mt-1">
                Interim targets: 50% reduction by 2030, 75% by 2040
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-green-600">{2050 - 2025}</p>
              <p className="text-sm text-gray-600">years remaining</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmissionsCard({ title, subtitle, value, color, methodology, warning }: any) {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    yellow: 'from-yellow-500 to-yellow-600',
    red: 'from-red-500 to-red-600',
  };

  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h4 className="text-sm font-medium text-gray-600">{title}</h4>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        <div className={`bg-gradient-to-br ${colors[color as keyof typeof colors]} rounded-lg p-2`}>
          <div className="text-white text-xl">📊</div>
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
      <p className="text-xs text-gray-500 mb-1">{methodology}</p>
      {warning && <p className="text-xs text-amber-600 mt-2">⚠️ {warning}</p>}
    </div>
  );
}

function PortfolioBar({ label, value, total, color }: any) {
  const percentage = (value / total * 100).toFixed(1);
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="font-medium">{(value / 1000000).toFixed(1)}M tCO2e ({percentage}%)</span>
      </div>
      <div className="bg-gray-200 rounded-full h-3">
        <div
          className={`bg-${color}-500 h-3 rounded-full`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function RiskCard({ title, exposure, percentage, icon }: any) {
  return (
    <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">${(exposure / 1000000000).toFixed(1)}B</p>
          <p className="text-sm text-gray-500 mt-1">{percentage}% of assets</p>
        </div>
        <div className="text-5xl">{icon}</div>
      </div>
    </div>
  );
}
